import React from 'react';
import {StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react';

import ActivityIndicator from '../../../../components/ui-controls/ActivityIndicator';
import Text from '../../../../components/ui-controls/Text';
import {useStores} from '../../../../stores/use-stores';
import theme from '../../../../style/theme';
import {OrderLine} from '../../../../transport/apiServiceResponse';
import {sum, toAUD, toAUDAbs} from '../../../../utils/functions';

// Extracted "as-is" from the original CheckoutScreen.tsx for
// potential reuse in Gift Cards Store but at the end it didn't
// happen

export const OrderTotals = observer(() => {
  const {cartStore} = useStores();

  const {totalAmount} = cartStore;

  const totalChargedOnGiftCards = cartStore.getTotalChargedOnGiftCardsCents();
  let totalChargedOnCreditCards = totalAmount - totalChargedOnGiftCards;

  const renderTotalLine = (
    name: string,
    amount: number,
    index: number | string = 0,
    signed = false,
  ) => (
    <View
      key={`${name}_${index}`}
      style={styles.totalRow}
      testID="checkout-screen-total-line">
      <Text style={[styles.totalName, styles.totalText]}>
        <Text style={{fontStyle: 'italic', fontWeight: '600'}}>{name}</Text>
      </Text>
      {cartStore.dirtyOrder ? (
        <View style={styles.activityHolder}>
          <ActivityIndicator size="small" />
        </View>
      ) : (
        <Text style={[styles.totalAmount, styles.totalText]}>
          {signed ? toAUD(amount, true) : toAUDAbs(amount, true)}
        </Text>
      )}
    </View>
  );

  return (
    <View style={styles.section} testID="checkout-screen-totals">
      {renderTotalLine(
        'Subtotal',
        cartStore.currentOrder?.order_lines
          ?.filter((ol: OrderLine) => !ol.charge)
          .map((ol: OrderLine) => ol.subtotal?.cents_inc_tax)
          .reduce(sum, 0) || 0,
      )}

      {renderTotalLine(
        'GST included',
        cartStore.currentOrder?.total?.cents_tax || 0,
      )}

      {!!totalChargedOnGiftCards &&
        renderTotalLine('Gift cards', totalChargedOnGiftCards)}
      {!!totalChargedOnGiftCards &&
        renderTotalLine('Credit/Debit card', totalChargedOnCreditCards)}

      {cartStore.currentOrder?.adjustments
        ? cartStore.currentOrder.adjustments
            ?.filter(
              (adj: any) =>
                !!adj.name && !adj.name.toLowerCase().includes('delivery'),
            )
            ?.map((adj: any, index: number) =>
              renderTotalLine(
                adj.name,
                adj.amount.cents_inc_tax,
                `${adj.name}_${index}`,
                true,
              ),
            )
        : null}

      {cartStore.currentOrder?.discounts_and_credits?.map(
        (dac: any, index: number) =>
          renderTotalLine(dac.narrative, -1 * dac.amount, index, true),
      )}

      {/* Ex: Free pickup: 0 */}
      {!cartStore.switchingDeliveryPickup
        ? cartStore.currentOrder?.order_lines
            ?.filter((ol: OrderLine) => ol.charge)
            .map((ol: OrderLine, index: number) =>
              renderTotalLine(ol.name, ol.total.cents_inc_tax, index),
            )
        : null}

      {renderTotalLine('Total', totalAmount)}
    </View>
  );
});

export default OrderTotals;

const styles = StyleSheet.create({
  webDesktopHorizontalContent: {
    flexDirection: 'row',
    gap: 16,
  },
  webDesktopSectionTitle: {
    color: theme.colour.text.heading.primary,
    fontSize: 16,
    fontWeight: 'bold',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 22,
    backgroundColor: 'white',
  },
  totalText: {
    textAlign: 'right',
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
  },
  totalName: {
    flex: 3,
  },
  totalAmount: {
    flex: 1,
  },
  activityHolder: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  cartItemSpacing: {
    marginBottom: 8,
  },
  rowCentered: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  bfButton: {
    marginVertical: 16,
  },
  label: {
    color: 'grey',
    fontSize: 12,
    marginBottom: 4,
  },
  section: {
    marginTop: 8,
  },
  placeOrderButton: {
    backgroundColor: theme.colour.background.button.primary,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 4,
    alignItems: 'center',
    marginTop: 8,
  },
  placeOrderButtonText: {
    color: 'white',
    fontWeight: '600',
  },
  buttonActivity: {
    position: 'absolute',
    marginLeft: 8,
  },
  confirmText: {
    marginVertical: 8,
    color: theme.colour.text.content.primary,
  },
});
